// src/contexts/LanguageContext.js
// Orchestrateur i18n : hydratation langue + réconciliation préférence membre /
// tenant + switchLanguage borné aux langues servies par /organization/locales
// (le catalogue plateforme — plus un filtre par tenant, cf. backend
// utils/locales.js availableLocales).
// Voir docs/PLAN_I18N_MULTILANGUE.md §6.2.
//
// Chaîne (miroir frontend de utils/resolveLocale.js backend) :
//   préférence membre (si supportée) → choix sauvegardé (localStorage) →
//   défaut tenant → 'fr'.
//
// DOIT être monté DANS OrganizationProvider (lit config.supportedLocales) et
// DANS le Provider Redux.

import React, { createContext, useContext, useEffect, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import i18n, { STORAGE_KEY } from '../i18n'
import { normalizeLocale, parseSupported } from '../utils/locales'
import { setCurrentLanguage, setTenantLocales } from '../store/languageSlice'
import { useOrganization } from './OrganizationContext'
import { updatePreferredLanguage } from '../services/preferencesService'

const LanguageContext = createContext()

export const useLanguage = () => {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used within a LanguageProvider')
  return ctx
}

export const LanguageProvider = ({ children }) => {
  const dispatch = useDispatch()
  const { config } = useOrganization()
  const current = useSelector((s) => s.language.current)
  const user = useSelector((s) => s.user?.user)

  const supported = useMemo(() => parseSupported(config?.supportedLocales), [config?.supportedLocales])
  const tenantDefault = useMemo(() => normalizeLocale(config?.defaultLocale), [config?.defaultLocale])
  const supportedKey = supported.join(',')

  // Au montage : aligner le store Redux sur la langue déjà initialisée par i18n
  // (depuis localStorage) — évite un décalage switcher/UI avant la réconciliation.
  useEffect(() => {
    dispatch(setCurrentLanguage(i18n.language))
  }, [dispatch])

  // Quand la config tenant arrive : exposer default + supported dans le store.
  useEffect(() => {
    dispatch(setTenantLocales({ default: tenantDefault, supported }))
  }, [dispatch, tenantDefault, supportedKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Réconciliation : préférence membre (si supportée) → localStorage → défaut tenant → fr.
  useEffect(() => {
    const hasSaved = typeof localStorage !== 'undefined' && !!localStorage.getItem(STORAGE_KEY)
    const saved = normalizeLocale(hasSaved ? localStorage.getItem(STORAGE_KEY) : null)
    const userPref = user?.preferred_language ? normalizeLocale(user.preferred_language) : null

    let next
    if (userPref && supported.includes(userPref)) next = userPref
    else if (hasSaved && supported.includes(saved)) next = saved
    else if (supported.includes(tenantDefault)) next = tenantDefault
    else next = 'fr'

    if (next !== i18n.language) i18n.changeLanguage(next)
    if (next !== current) dispatch(setCurrentLanguage(next))
  }, [user?.preferred_language, tenantDefault, supportedKey]) // eslint-disable-line react-hooks/exhaustive-deps

  // Contrainte #3 : ne JAMAIS appliquer/persister une langue hors de la liste
  // servie par l'API. Le backend refuse de son côté (400 PREFERRED_LANGUAGE_INVALID
  // sur un code hors catalogue) ; ce garde-fou évite d'appliquer localement une
  // langue que le serveur rejettera, ce qui désynchroniserait l'UI de la préférence.
  const switchLanguage = useCallback(
    (lang) => {
      const norm = normalizeLocale(lang)
      if (!supported.includes(norm)) return
      if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, norm)
      i18n.changeLanguage(norm)
      dispatch(setCurrentLanguage(norm))
      // Persistance backend désactivée en S1 (no-op silencieux), localStorage suffit.
      updatePreferredLanguage(norm, user?.id).catch(() => {})
    },
    [dispatch, supported, user?.id],
  )

  const value = useMemo(
    () => ({ current, supported, switchLanguage }),
    [current, supported, switchLanguage],
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}
